<script>
import appConfig from '@src/app.config'
import { StaffApiService } from '@common/ApiService'
import { logout } from '@common/AuthHelper'
import JwtService from '@common/JwtService'
import AutoLogoutService from '@common/AutoLogoutService'
import '@common/VeeValidateCustomRules'
import Layout from '@layouts/main'
import { useIdle } from '@vueuse/core'
import { watch } from 'vue'

const { idle, reset } = useIdle(1000)

watch(idle, (idleValue) => {
  if (!idleValue) {
    AutoLogoutService.autoLogout()

    reset()
  }
})

const OPEN_TABS = 'OPEN_TABS'
const BEFORE_DECREMENT = 'BEFORE_DECREMENT'
const ID_ACTIVE_TIME = 'LAST_ACTIVE_TIME'

export default {
  components: { Layout },

  head: {
    titleTemplate(title) {
      title = typeof title === 'function' ? title(this.$store) : title
      return title ? `${title} | ${appConfig.title}` : appConfig.title
    },
  },

  mounted() {
    StaffApiService.setInterceptors()
    AutoLogoutService.autoLogout()
  },

  created() {
    window.addEventListener('beforeunload', this.decrementOpenTabs)
    window.addEventListener('DOMContentLoaded', this.incrementOpenTabs())
    window.addEventListener('storage', this.onStorageChange)
  },

  beforeUnmount() {
    window.removeEventListener('storage', this.onStorageChange)
  },

  methods: {
    incrementOpenTabs() {
      let beforeDecrement = window.localStorage.getItem(BEFORE_DECREMENT)
      let openedTabs = window.localStorage.getItem(OPEN_TABS)
      let logOut = false

      if (isNaN(openedTabs) || !openedTabs || openedTabs <= 0 || beforeDecrement === 1) {
        openedTabs = 0
        logOut = true
      } else {
        openedTabs = parseInt(openedTabs)
      }

      window.localStorage.setItem(OPEN_TABS, openedTabs + 1)

      if (logOut && JwtService.getToken()) {
        this.logoutUser()
      }
    },

    decrementOpenTabs() {
      let openedTabs = window.localStorage.getItem(OPEN_TABS)

      if (isNaN(openedTabs) || !openedTabs || openedTabs <= 1 || AutoLogoutService.lastActiveTimeInPast()) {
        window.localStorage.setItem(BEFORE_DECREMENT, 1)
        window.localStorage.removeItem(OPEN_TABS)
      } else {
        window.localStorage.setItem(BEFORE_DECREMENT, openedTabs)
        openedTabs = parseInt(openedTabs - 1)
        window.localStorage.setItem(OPEN_TABS, openedTabs)
      }

      AutoLogoutService.setLastActiveTime()
    },

    logoutUser() {
      const lastActivityTime = Date.parse(window.localStorage.getItem(ID_ACTIVE_TIME))

      let date = new Date()
      date.setSeconds(date.getSeconds() - 3)

      if (lastActivityTime < date) {
        logout()
      }
    },

    onStorageChange(event) {
      if (event.key === 'DEVICE_ID' && event.newValue === null && JwtService.getToken()) {
        logout()
      }
    },
  },
}
</script>

<template>
  <div id="app">
    <Layout>
      <RouterView :key="$route.fullPath" />
    </Layout>
  </div>
</template>

<!-- This should generally be the only global CSS in the app. -->
<style lang="scss">
// stylelint-disable selector-max-type, selector-class-pattern
@import '~nprogress/nprogress.css';
@import '@variables';
@import '@design/transitions.scss';
@import '@design/override.scss';
@import '@design/global.scss';
@import '@design/graphs.scss';
@import '@design/vue-select.scss';
@import '~bootstrap/scss/bootstrap';
@import '~bootstrap-vue/src/index.scss';
@import 'vue-select/src/scss/vue-select.scss';
@import '@design/datepicker-calendar.scss';

// ===
// Vendor
// ===

#nprogress {
  .bar {
    height: 5px;
    background: $progress-color;

    &::before {
      display: none;
    }
  }

  .spinner {
    top: 8px;
    left: 6px;
  }

  .spinner-icon {
    border-top-color: $progress-color;
    border-left-color: $progress-color;
  }
}

label.required::after,
legend.required::after {
  color: red;
  content: ' *';
}

.vdp-datepicker__calendar .cell.selected {
  color: white !important;
  background: $primary !important;
}
.vdp-datepicker__calendar .cell:not(.blank):not(.disabled).day:hover,
.vdp-datepicker__calendar .cell:not(.blank):not(.disabled).month:hover,
.vdp-datepicker__calendar .cell:not(.blank):not(.disabled).year:hover {
  border: 1px solid $primary !important;
}

.thead-primary {
  color: $white;
  background: $primary;
}
</style>
